import type { CSSProperties, MouseEvent, PointerEvent } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

export interface Pestana {
  ruta: string
  /** Nombre del PNG en /icons/pestanas */
  icono: string
  texto: string
}

/*
 * Barra de pestanas flotante con burbuja de cristal, al estilo de iOS 26.
 *
 * En reposo la pestana activa lleva una pildora suave detras. Al arrastrar el
 * dedo por la barra la pildora se vuelve una lupa: crece, se sale de la barra
 * y lo que queda debajo se ve mas grande y en color. Al soltar se encaja en la
 * pestana mas cercana y navega. Con un toque normal la lupa viaja de una
 * pestana a otra y se asienta.
 *
 * Safari no deja deformar lo que hay detras de un elemento (el
 * `backdrop-filter: url(#svg)` solo existe en Chrome), asi que la lupa no
 * refracta el fondo: lleva dentro una segunda copia de la fila de pestanas,
 * del color de acento, que se contrarresta el desplazamiento para quedar
 * calcada sobre la de verdad. Al escalar la burbuja se escala esa copia
 * alrededor del centro de la lupa, que es justo lo que hace una lupa.
 */

// Lo que hay que mover el dedo para que un toque pase a ser arrastre
const UMBRAL = 8
// Lo que se queda la lupa encendida tras un toque o al soltar. Algo mas que
// el muelle del CSS, para que se apague ya encajada y no a medio viaje.
const VIAJE_MS = 460

function indiceActivo(pestanas: Pestana[], ruta: string) {
  return pestanas.findIndex((p) =>
    p.ruta === '/' ? ruta === '/' : ruta === p.ruta || ruta.startsWith(p.ruta + '/'),
  )
}

function Icono({ nombre }: { nombre: string }) {
  return (
    <span
      className="icono"
      style={{ '--icono': `url(/icons/pestanas/${nombre}.png)` } as CSSProperties}
    />
  )
}

export default function BarraPestanas({
  pestanas,
  algoRoto,
}: {
  pestanas: Pestana[]
  /** Punto rojo sobre Ajustes */
  algoRoto: boolean
}) {
  const { pathname } = useLocation()
  const navegar = useNavigate()
  const activa = indiceActivo(pestanas, pathname)

  const fila = useRef<HTMLDivElement>(null)
  const [ancho, setAncho] = useState(0)
  // Posicion del dedo dentro de la fila mientras se arrastra; null si no
  const [dedo, setDedo] = useState<number | null>(null)
  const [viajando, setViajando] = useState(false)

  const gesto = useRef<{ id: number; x0: number; arrastrando: boolean } | null>(null)
  const finArrastre = useRef(-Infinity)
  const reloj = useRef<ReturnType<typeof setTimeout>>(undefined)

  useLayoutEffect(() => {
    const el = fila.current
    if (!el) return
    const medir = () => setAncho(el.clientWidth)
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [])

  function viajar() {
    setViajando(true)
    clearTimeout(reloj.current)
    reloj.current = setTimeout(() => setViajando(false), VIAJE_MS)
  }

  useEffect(() => () => clearTimeout(reloj.current), [])

  // Cambio de pestana, sea por toque, por arrastre o por un enlace de dentro.
  // Si se viene de fuera de las pestanas (una ficha de manga) no hay viaje:
  // la burbuja estaba escondida y simplemente aparece.
  const anterior = useRef(activa)
  useEffect(() => {
    const venia = anterior.current
    anterior.current = activa
    if (venia !== activa && venia >= 0 && activa >= 0) viajar()
  }, [activa])

  const celda = pestanas.length ? ancho / pestanas.length : 0
  const limitar = (x: number) => Math.min(Math.max(x, celda / 2), ancho - celda / 2)

  function alPulsar(e: PointerEvent<HTMLElement>) {
    if (!e.isPrimary || e.button !== 0) return
    gesto.current = { id: e.pointerId, x0: e.clientX, arrastrando: false }
  }

  function alMover(e: PointerEvent<HTMLElement>) {
    const g = gesto.current
    if (!g || g.id !== e.pointerId || !fila.current) return
    if (!g.arrastrando) {
      if (Math.abs(e.clientX - g.x0) < UMBRAL) return
      g.arrastrando = true
      // Se captura solo al empezar a arrastrar: capturando desde el primer
      // toque, el clic caeria en la barra y no en el enlace, y los toques
      // normales dejarian de navegar.
      e.currentTarget.setPointerCapture(e.pointerId)
    }
    setDedo(limitar(e.clientX - fila.current.getBoundingClientRect().left))
  }

  function alSoltar(e: PointerEvent<HTMLElement>) {
    const g = gesto.current
    if (!g || g.id !== e.pointerId) return
    gesto.current = null
    if (!g.arrastrando || !fila.current) return

    finArrastre.current = e.timeStamp
    setDedo(null)
    viajar()
    if (e.type !== 'pointerup') return

    const x = e.clientX - fila.current.getBoundingClientRect().left
    const i = Math.min(pestanas.length - 1, Math.max(0, Math.floor(x / celda)))
    const destino = pestanas[i]
    if (destino && i !== activa) navegar(destino.ruta)
  }

  // Tras un arrastre el navegador aun manda su clic, y caeria en la pestana
  // donde empezo el dedo, deshaciendo lo que se acaba de elegir.
  function alHacerClic(e: MouseEvent<HTMLElement>) {
    if (e.timeStamp - finArrastre.current < 500) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const lupa = dedo !== null || viajando
  const visible = ancho > 0 && (dedo !== null || activa >= 0)
  const x = dedo ?? (Math.max(activa, 0) + 0.5) * celda

  const clases = ['burbuja']
  if (lupa) clases.push('lupa')
  if (dedo !== null) clases.push('siguiendo')
  if (!visible) clases.push('oculta')

  return (
    <nav
      className={lupa ? 'barra con-lupa' : 'barra'}
      onPointerDown={alPulsar}
      onPointerMove={alMover}
      onPointerUp={alSoltar}
      onPointerCancel={alSoltar}
      onClickCapture={alHacerClic}
    >
      <div className="barra-fila" ref={fila}>
        {pestanas.map((p) => (
          <NavLink
            key={p.ruta}
            to={p.ruta}
            end={p.ruta === '/'}
            className={({ isActive }) => (isActive ? 'activa' : undefined)}
          >
            <Icono nombre={p.icono} />
            {p.ruta === '/ajustes' && algoRoto && <span className="chincheta" />}
            {p.texto}
          </NavLink>
        ))}

        <div
          className={clases.join(' ')}
          aria-hidden="true"
          style={
            {
              '--x': `${x}px`,
              '--celda': `${celda}px`,
              '--ancho': `${ancho}px`,
            } as CSSProperties
          }
        >
          <div className="burbuja-fila">
            {pestanas.map((p) => (
              <span key={p.ruta} className="burbuja-pestana">
                <Icono nombre={p.icono} />
                {p.texto}
              </span>
            ))}
          </div>
        </div>
      </div>
    </nav>
  )
}
